import { useMemo } from 'react';
import { useParams, useLocation, useHistory, useRouteMatch } from 'react-router-dom';
import camelCase from 'lodash/camelCase';

import { GenericObject } from '../../types';

//todo: fix this change the name to index to Short the path
// Hook
/**
 * A custom hook that provides a unified interface for React Router functionality.
 * It simplifies URL parameter access and provides enhanced navigation methods.
 * @returns {Object} An object containing navigation methods and current route state.
 * @property {Function} push - Standard history push method.
 * @property {Function} safePush - Navigates to a path only if a valid string is provided.
 * @property {Function} pushSaveSearch - Navigates while preserving current URL query parameters.
 * @property {Object} queryObject - Current URL search parameters parsed into a camelCase object.
 * @property {string} pathname - Current location pathname.
 */
export function useRouter() {
    const params = useParams();
    const location = useLocation();
    const history = useHistory();
    const match = useRouteMatch();

    // Return our custom router object
    // Memoize so that a new object is only returned if something changes
    return useMemo(() => {
        const urlParams = new URLSearchParams(location.search);
        const queryObject: GenericObject<string> = {};
        urlParams.forEach((value, key) => {
            queryObject[camelCase(key)] = value;
        });
        /**
         * Navigates to a new URL while keeping the existing search query.
         * Useful for pagination or filtering across different views.
         */
        const pushSaveSearch = (url?: string | null | undefined) => {
            if (url) {
                history.push({ pathname: url, search: location.search });
            }
        };
        /**
         * Safely performs a history.push only if a valid path is provided.
         * Prevents accidental navigation to 'undefined' or empty strings.
         */
        const safePush = (path?: string | null | undefined) => {
            if (path) history.push(path);
        };
        return {
            urlParams,
            push: history.push,
            replace: history.replace,
            pathname: location.pathname,
            pushSaveSearch,
            cbPushSaveSearch: (url?: string) => () => pushSaveSearch(url),
            safePush,
            queryObject,
            // Include match, location, history objects so we have
            // access to extra React Router functionality if needed.
            params,
            match,
            location,
            history
        };
    }, [params, match, location, history]);
}
